/**
 * The Connections tab as data: every connector the workspace has, could have,
 * or could ask for, as one record kind, grouped by what the reader owes it
 * (Needs you, Your accounts, Connected, Ready to connect, Early access), with
 * the status word the app uses, the health line, and the two verbs the
 * record's footer carries. Pure, so grouping and the verb table are tested
 * without a network.
 */
import type {
  ConnectedService,
  ConnectedServiceDefinition,
  ConnectedServiceType,
  ConnectorDraft,
} from "@yagni/shared";
import {
  CATALOG_CATEGORY_ORDER,
  CONNECTED_SERVICE_CATEGORIES,
  CONNECTOR_CATALOG,
  PLANNED_CONNECTOR_CATEGORIES,
  getConnectedServiceDefinition,
  getConnectorAvailability,
  getConnectorCatalogEntry,
  listRequestableConnectors,
  type ConnectorCatalogEntry,
} from "@yagni/shared";

export type ConnectorHealthState = "healthy" | "stale" | "error";

export interface ConnectorStatusRow {
  source: string;
  label: string;
  state: ConnectorHealthState;
  lastSyncAt: string | null;
  minutesBehind: number | null;
  thresholdMinutes: number;
  lastError?: { message: string; at: string };
}

export type ServiceHealthState = ConnectorHealthState | "syncing" | "unknown";

/**
 * Connected service types whose backend source declares containers, so the
 * channel visibility list is worth showing. Google (personal), HubSpot and
 * custom connectors have none.
 */
export const CONTAINER_BEARING_SERVICE_TYPES = new Set<ConnectedServiceType>([
  "slack",
  "linear",
  "notion",
  "github",
  "stripe",
  "trello",
  "confluence",
  "jira",
]);

/** The connector `source` ids behind a connected-service type. */
export function sourcesForServiceType(serviceType: ConnectedServiceType): string[] {
  if (serviceType === "google_workspace") return ["gmail", "gcal", "google_drive"];
  return [serviceType];
}

export function summarizeHealth(rows: readonly ConnectorStatusRow[]): ServiceHealthState {
  if (rows.length === 0) return "unknown";
  if (rows.some((row) => row.state === "error")) return "error";
  if (rows.some((row) => row.state === "stale")) return "stale";
  if (rows.every((row) => row.lastSyncAt === null)) return "syncing";
  return "healthy";
}

export function timeAgo(isoString: string, now = Date.now()): string {
  const minutes = Math.floor((now - new Date(isoString).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function latestSyncTimeAgo(rows: readonly ConnectorStatusRow[]): string {
  const latest = rows
    .map((row) => row.lastSyncAt)
    .filter((value): value is string => value !== null)
    .sort()
    .at(-1);
  return latest ? timeAgo(latest) : "pending";
}

/** "gmail.readInbox" → "Read Inbox". */
export function capabilityLabel(capability: string): string {
  const after = capability.includes(".") ? capability.split(".").pop()! : capability;
  return after
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Every LIVE service type a workspace can connect in one step. Early-access
 * catalog entries never get a Connect record; they are requested instead.
 */
export const LIVE_SERVICE_TYPES: ConnectedServiceType[] = CONNECTED_SERVICE_CATEGORIES.flatMap((c) =>
  c.services.filter((s) => s !== "custom"),
).filter((serviceType) => {
  const entry = getConnectorCatalogEntry(serviceType);
  return !entry || getConnectorAvailability(entry) === "live";
});

/** One not-yet-live integration a person can ask for. */
export interface RequestableIntegration {
  id: string;
  label: string;
  description: string;
  categoryLabel: string;
  categoryRank: number;
  /** `early_access` is built with its install path pending; `request` is a wish. */
  stage: "early_access" | "request";
}

const REQUEST_CATEGORY_LABELS: string[] = (() => {
  const labels = PLANNED_CONNECTOR_CATEGORIES.map((category) => category.label);
  for (const category of CATALOG_CATEGORY_ORDER) {
    if (!labels.includes(category.label)) labels.push(category.label);
  }
  return labels;
})();

function requestCategoryRank(label: string): number {
  const index = REQUEST_CATEGORY_LABELS.indexOf(label);
  return index === -1 ? REQUEST_CATEGORY_LABELS.length : index;
}

const PLANNED_CATEGORY_LABEL_BY_KEY = new Map(PLANNED_CONNECTOR_CATEGORIES.map((c) => [c.key, c.label]));
const CATALOG_CATEGORY_LABEL_BY_KEY = new Map(CATALOG_CATEGORY_ORDER.map((c) => [c.key, c.label]));

export function buildRequestableIntegrations(connectedTypes: ReadonlySet<string>): RequestableIntegration[] {
  const items: RequestableIntegration[] = [];
  for (const entry of CONNECTOR_CATALOG as readonly ConnectorCatalogEntry[]) {
    if (getConnectorAvailability(entry) === "live") continue;
    if (connectedTypes.has(entry.id)) continue;
    const categoryLabel = CATALOG_CATEGORY_LABEL_BY_KEY.get(entry.category) ?? "Other";
    items.push({
      id: entry.id,
      label: entry.label,
      description: entry.description,
      categoryLabel,
      categoryRank: requestCategoryRank(categoryLabel),
      stage: "early_access",
    });
  }
  for (const planned of listRequestableConnectors()) {
    if (connectedTypes.has(planned.id)) continue;
    const categoryLabel = PLANNED_CATEGORY_LABEL_BY_KEY.get(planned.category) ?? "Other";
    items.push({
      id: planned.id,
      label: planned.label,
      description: planned.description,
      categoryLabel,
      categoryRank: requestCategoryRank(categoryLabel),
      stage: planned.built ? "early_access" : "request",
    });
  }
  return items.sort((a, b) => a.categoryRank - b.categoryRank || a.label.localeCompare(b.label));
}

/** The connector's category as the row's key. */
export function categoryLabel(serviceType: ConnectedServiceType): string {
  if (serviceType === "custom") return "Custom";
  const entry = getConnectorCatalogEntry(serviceType);
  return (entry && CATALOG_CATEGORY_LABEL_BY_KEY.get(entry.category)) ?? "Other";
}

/** The status words the app uses for a connection. */
export type ConnectorStatus =
  | "connected"
  | "attention"
  | "stale"
  | "syncing"
  | "pending"
  | "personal"
  | "link"
  | "ready"
  | "early"
  | "wish";

export const STATUS_WORD: Record<ConnectorStatus, string> = {
  connected: "Connected",
  attention: "Needs attention",
  stale: "Catching up",
  syncing: "Indexing",
  pending: "Pending setup",
  personal: "Connected",
  link: "Not linked",
  ready: "Ready",
  early: "Early access",
  wish: "Planned",
};

/** The status word's hue, as a token. */
export const STATUS_HUE: Record<ConnectorStatus, string> = {
  connected: "var(--accent-active)",
  attention: "var(--honesty)",
  stale: "var(--honesty)",
  syncing: "var(--accent-active)",
  pending: "var(--accent)",
  personal: "var(--accent-active)",
  link: "var(--accent)",
  ready: "var(--text-quiet)",
  early: "var(--text-quiet)",
  wish: "var(--text-quiet)",
};

/** One record on the Connections tab. */
export type ConnectorRecord =
  | {
      kind: "service";
      id: string;
      status: Extract<ConnectorStatus, "connected" | "attention" | "stale" | "syncing" | "personal">;
      serviceType: ConnectedServiceType;
      service: ConnectedService;
      definition: ConnectedServiceDefinition;
      name: string;
      statusRows: ConnectorStatusRow[];
    }
  | {
      kind: "link";
      id: string;
      status: "link";
      serviceType: ConnectedServiceType;
      /** The workspace connection the viewer has not linked their own account to. */
      service: ConnectedService;
      definition: ConnectedServiceDefinition;
      name: string;
    }
  | { kind: "draft"; id: string; status: "pending"; draft: ConnectorDraft; name: string }
  | {
      kind: "template";
      id: string;
      status: "ready";
      serviceType: ConnectedServiceType;
      definition: ConnectedServiceDefinition;
      name: string;
    }
  | { kind: "request"; id: string; status: "early" | "wish"; item: RequestableIntegration; name: string };

export interface ConnectorGroups {
  needs: ConnectorRecord[];
  yours: ConnectorRecord[];
  connected: ConnectorRecord[];
  ready: ConnectorRecord[];
  early: ConnectorRecord[];
  /** Planned, not built: named in the dashed line under the list. */
  planned: ConnectorRecord[];
}

// GitHub leads (the engineering wedge).
const SERVICE_TYPE_PRIORITY: ConnectedServiceType[] = ["github", "google_workspace", "stripe", "hubspot", "notion"];
function typeRank(serviceType: ConnectedServiceType | null): number {
  const index = serviceType ? SERVICE_TYPE_PRIORITY.indexOf(serviceType) : -1;
  return index === -1 ? SERVICE_TYPE_PRIORITY.length : index;
}

function serviceStatus(service: ConnectedService, rows: readonly ConnectorStatusRow[]): ConnectorRecord["status"] {
  if (service.connectionScope === "personal") return "personal";
  const health = summarizeHealth(rows);
  if (health === "error") return "attention";
  if (health === "stale") return "stale";
  if (health === "syncing") return "syncing";
  return "connected";
}

/** A personal connection: the viewer's own write grant, or Google (personal only). */
export function isPersonal(record: ConnectorRecord): boolean {
  if (record.kind === "link") return true;
  if (record.kind !== "service") return false;
  return record.service.connectionScope === "personal" || record.serviceType === "google_workspace";
}

export interface ConnectorInputs {
  services: readonly ConnectedService[];
  drafts: readonly ConnectorDraft[];
  statusRows: readonly ConnectorStatusRow[];
}

export function buildConnectorRecords({ services, drafts, statusRows }: ConnectorInputs): ConnectorRecord[] {
  const bySource = new Map(statusRows.map((row) => [row.source, row]));
  const personal = services.filter((s) => s.connectionScope === "personal");
  const personalTypes = new Set(personal.map((s) => s.serviceType));
  const workspace = services.filter((s) => s.connectionScope !== "personal");
  const connectedTypes = new Set(workspace.filter((s) => s.serviceType !== "custom").map((s) => s.serviceType));

  const records: ConnectorRecord[] = [];
  for (const service of services) {
    const definition = getConnectedServiceDefinition(service.serviceType);
    const rows =
      service.connectionScope === "personal"
        ? []
        : sourcesForServiceType(service.serviceType)
            .map((source) => bySource.get(source))
            .filter((row): row is ConnectorStatusRow => row !== undefined);
    records.push({
      kind: "service",
      id: `service:${service.id}`,
      status: serviceStatus(service, rows) as Extract<ConnectorRecord, { kind: "service" }>["status"],
      serviceType: service.serviceType,
      service,
      definition,
      name: service.customConfig?.serviceName ?? definition.label,
      statusRows: rows,
    });
  }

  const linkSeen = new Set<string>();
  for (const service of workspace) {
    if (personalTypes.has(service.serviceType) || linkSeen.has(service.serviceType)) continue;
    if (getConnectorCatalogEntry(service.serviceType)?.writeIdentity !== "personal") continue;
    linkSeen.add(service.serviceType);
    const definition = getConnectedServiceDefinition(service.serviceType);
    records.push({ kind: "link", id: `link:${service.serviceType}`, status: "link", serviceType: service.serviceType, service, definition, name: definition.label });
  }

  for (const draft of drafts) {
    records.push({ kind: "draft", id: `draft:${draft.id}`, status: "pending", draft, name: draft.serviceName });
  }

  for (const serviceType of LIVE_SERVICE_TYPES) {
    if (connectedTypes.has(serviceType)) continue;
    // A personal-only connector (Google) that the viewer already holds is not "ready".
    if (serviceType === "google_workspace" && services.some((s) => s.serviceType === "google_workspace")) continue;
    const definition = getConnectedServiceDefinition(serviceType);
    records.push({ kind: "template", id: `template:${serviceType}`, status: "ready", serviceType, definition, name: definition.label });
  }

  for (const item of buildRequestableIntegrations(new Set(services.map((s) => s.serviceType)))) {
    records.push({ kind: "request", id: `request:${item.id}`, status: item.stage === "early_access" ? "early" : "wish", item, name: item.label });
  }
  return records;
}

export function groupConnectors(records: readonly ConnectorRecord[]): ConnectorGroups {
  const groups: ConnectorGroups = { needs: [], yours: [], connected: [], ready: [], early: [], planned: [] };
  for (const record of records) {
    if (record.status === "attention" || record.status === "pending") groups.needs.push(record);
    else if (isPersonal(record)) groups.yours.push(record);
    else if (record.kind === "service") groups.connected.push(record);
    else if (record.kind === "template") groups.ready.push(record);
    else if (record.status === "early") groups.early.push(record);
    else groups.planned.push(record);
  }
  const byPriority = (a: ConnectorRecord, b: ConnectorRecord) =>
    typeRank("serviceType" in a ? a.serviceType : null) - typeRank("serviceType" in b ? b.serviceType : null) ||
    a.name.localeCompare(b.name);
  groups.needs.sort(byPriority);
  groups.yours.sort(byPriority);
  groups.connected.sort(byPriority);
  groups.ready.sort((a, b) => keyOf(a).localeCompare(keyOf(b)) || byPriority(a, b));
  return groups;
}

/** The row's key: the connector's category. */
export function keyOf(record: ConnectorRecord): string {
  if (isPersonal(record)) return "Your account";
  if (record.kind === "draft") return "Custom";
  if (record.kind === "request") return record.item.categoryLabel;
  return categoryLabel(record.serviceType);
}

function brokenLine(row: ConnectorStatusRow): string {
  if (row.state === "error" && row.lastError) return `${row.label}: ${row.lastError.message}`;
  if (row.state === "stale" && row.minutesBehind !== null) return `${row.label}: ${row.minutesBehind} min behind`;
  return `${row.label}: waiting on the first sync`;
}

/** The health line under the name: what is true about it right now. */
export function healthLine(record: ConnectorRecord, extra: { repoCount?: number | null } = {}): string {
  switch (record.kind) {
    case "service": {
      const { service, statusRows } = record;
      if (service.connectionScope === "personal") return `Your ${record.definition.label} account is linked for writing`;
      const facts: string[] = [];
      if (record.serviceType === "google_workspace" && service.accountEmail) facts.push(service.accountEmail);
      if (record.serviceType === "jira" && service.accountLabel) facts.push(`Site ${service.accountLabel}`);
      if (record.serviceType === "github" && extra.repoCount != null) {
        facts.push(`${extra.repoCount} ${extra.repoCount === 1 ? "repository" : "repositories"}`);
      }
      if (service.customConfig) facts.push(`${service.customConfig.baseUrl}, ${service.customConfig.authScheme} auth`);
      const broken = statusRows.filter((row) => row.state !== "healthy" || row.lastSyncAt === null);
      if (broken.length > 0) {
        facts.push(brokenLine(broken[0]!));
      } else if (statusRows.length > 0) {
        facts.push(`Last sync ${latestSyncTimeAgo(statusRows)}`);
      }
      return facts.join(" · ") || record.definition.description;
    }
    case "link":
      return "Your workspace is connected. Link your own account so writes are attributed to you";
    case "draft":
      return `Waiting on an API key · ${record.draft.baseUrl}, ${record.draft.authScheme} auth`;
    case "template":
      return record.definition.description;
    case "request":
      return record.item.description;
  }
}

/** The word on the row's hover button. */
export function rowVerb(record: ConnectorRecord, requested = false): string {
  switch (record.status) {
    case "attention":
      return "Fix";
    case "pending":
      return "Finish";
    case "link":
      return "Link";
    case "ready":
      return "Connect";
    case "early":
    case "wish":
      return requested ? "Requested" : "Request";
    default:
      return "Open";
  }
}

export type ConnectorVerb =
  | "disconnect"
  | "reconnect"
  | "dismiss"
  | "add_key"
  | "retry_sync"
  | "sync_now"
  | "use_key"
  | "connect"
  | "link_account"
  | "request";

export const CONNECTOR_VERB_LABEL: Record<ConnectorVerb, string> = {
  disconnect: "Disconnect",
  reconnect: "Reconnect",
  dismiss: "Dismiss",
  add_key: "Add API key",
  retry_sync: "Retry sync",
  sync_now: "Sync now",
  use_key: "Use API key",
  connect: "Connect",
  link_account: "Connect your account",
  request: "Request early access",
};

/**
 * The footer by status: attention is Disconnect / Reconnect, pending is
 * Dismiss / Add API key, catching up is Disconnect / Retry sync, connected is
 * Disconnect / Sync now, ready is Use API key / Connect, early access is
 * Request early access. A connection with nothing to sync has no Sync now;
 * a ready connector with no key form has no Use API key.
 */
export function connectorFooter(
  record: ConnectorRecord,
): { secondary: ConnectorVerb | null; primary: ConnectorVerb | null } {
  switch (record.status) {
    case "attention":
      return { secondary: "disconnect", primary: "reconnect" };
    case "pending":
      return { secondary: "dismiss", primary: "add_key" };
    case "stale":
      return { secondary: "disconnect", primary: "retry_sync" };
    case "connected":
    case "syncing": {
      const canSync = record.kind === "service" && (record.statusRows.length > 0 || record.serviceType === "github");
      return { secondary: "disconnect", primary: canSync ? "sync_now" : null };
    }
    case "personal":
      return { secondary: "disconnect", primary: null };
    case "link":
      return { secondary: null, primary: "link_account" };
    case "ready": {
      const hasKeyForm = record.kind === "template" && record.definition.credentialFields.length > 0;
      const oauth = record.kind === "template" && (record.definition.oauthFlow || record.serviceType === "github" || record.serviceType === "google_workspace");
      return { secondary: hasKeyForm && oauth ? "use_key" : null, primary: "connect" };
    }
    case "early":
    case "wish":
      return { secondary: null, primary: "request" };
  }
}

/** The connector mark's colour, as a token. Not-yet-real marks are quiet. */
export function markColor(record: ConnectorRecord): string {
  if (record.kind === "request" || record.kind === "template") return "var(--text-quiet)";
  if (record.kind === "draft") return "var(--accent)";
  switch (record.serviceType) {
    case "github":
      return "var(--stage-done)";
    case "jira":
      return "var(--agent-analytics)";
    case "slack":
      return "var(--agent-comms)";
    case "linear":
      return "var(--agent-finance)";
    case "google_workspace":
      return "var(--agent-support)";
    case "sentry":
    case "bugsnag":
    case "datadog":
      return "var(--agent-gtm)";
    case "notion":
      return "var(--stage-done)";
    case "stripe":
      return "var(--stage-plan)";
    case "hubspot":
      return "var(--agent-ops)";
    default:
      return "var(--stage-proposed)";
  }
}
